/**
 * Configurações › Integrações › Widgets (D429 W-EMBED-ADMIN, program D425, ADR-0067 + Amendment D429) — the framework-
 * neutral, tested view-model of the Central widget's embeds: the editable form, its validation with the SAME rules the
 * server applies (it calls the `@vello/chat-widget` kernel directly — origin normalisation, plain-text name and consent,
 * the settings shape), the minimal PATCH, the mapping of the server's `WIDGET_*` refusals to form fields, the operator's
 * authority from its grants, and the state of the copyable snippet.
 *
 * The console only FILTERS and HELPS: the server re-validates and re-authorizes every call (`widget:view` to read,
 * `widget:manage` to write — PLATFORM words, so only a GLOBAL grant carries them). Neutral: no host, country, language or
 * script URL is written here — the snippet comes from the server, which builds it from its own configuration.
 */
import { type Result, ok, err } from '@vello/core';
import { type CreateWidgetEmbedInput, type UpdateWidgetEmbedInput, type WidgetEmbedView, type WidgetAudience, type WidgetPosition, type WidgetTopic } from '@vello/api-client';
import {
  WIDGET_CONSENT_TEXT_MAX_CHARS,
  WIDGET_MAX_ORIGINS,
  WIDGET_NAME_MAX_CHARS,
  WIDGET_POSITIONS,
  WIDGET_AUDIENCES,
  normalizeEmbedMarket,
  normalizeEmbedSettings,
  topicsOf,
  normalizeOrigin,
  validatePlainText,
} from '@vello/chat-widget';
import { grantsAllow, type KnownGrants } from './console-access.js';

export { WIDGET_AUDIENCES as WIDGET_FORM_AUDIENCES, WIDGET_POSITIONS as WIDGET_FORM_POSITIONS, WIDGET_MAX_ORIGINS as WIDGET_FORM_MAX_ORIGINS };
export const WIDGET_FORM_NAME_MAX = WIDGET_NAME_MAX_CHARS;
export const WIDGET_FORM_CONSENT_MAX = WIDGET_CONSENT_TEXT_MAX_CHARS;

/** The embed form as the operator types it. Blank origin rows are ignored; blank locale/consent mean "not set". */
export interface WidgetForm {
  readonly name: string;
  readonly origins: readonly string[];
  /**
   * D853: `country` - ONE embed for the whole country, each conversation resolves its own city (the default); `city` - a partner fixed to one city (needs `marketId`,
   * the D850 rule). An existing embed keeps its scope: a city embed cannot lose its market.
   */
  readonly scope: WidgetScope;
  /** D853: the country a `country` embed serves (ISO 3166-1 alpha-2); empty = not chosen yet (the form refuses it). */
  readonly countryCode: string;
  /** D850: the market a `city` embed resolves at (`cc:slug`); empty = not chosen yet (the form refuses it). */
  readonly marketId: string;
  readonly position: WidgetPosition;
  /** D625: who the widget serves (each audience brings its topics from the catalog). */
  readonly audiences: readonly WidgetAudience[];
  readonly locale: string;
  readonly consentText: string;
}

export type WidgetScope = 'country' | 'city';
export type WidgetFormField = 'name' | 'origins' | 'countryCode' | 'marketId' | 'audiences' | 'locale' | 'consentText' | 'settings';
export type WidgetFormIssueCode =
  | 'NAME_INVALID'
  | 'ORIGIN_MALFORMED'
  | 'ORIGIN_WILDCARD'
  | 'ORIGIN_INSECURE'
  | 'ORIGINS_EMPTY'
  | 'TOO_MANY_ORIGINS'
  | 'MARKET_REQUIRED'
  | 'MARKET_INVALID'
  | 'COUNTRY_REQUIRED'
  | 'COUNTRY_INVALID'
  | 'AUDIENCES_EMPTY'
  | 'LOCALE_INVALID'
  | 'CONSENT_INVALID'
  | 'SETTINGS_INVALID';

export interface WidgetFormIssue {
  readonly field: WidgetFormField;
  readonly code: WidgetFormIssueCode;
  /** The origin row (0-based, in the form's order) an origin issue belongs to. */
  readonly index?: number;
}

/** The topics an audience brings to the widget (the catalog's, the same in every widget) — shown under its checkbox. */
export function widgetAudienceTopics(audience: WidgetAudience): readonly WidgetTopic[] {
  return topicsOf(audience);
}

export function emptyWidgetForm(): WidgetForm {
  return { name: '', origins: [''], scope: 'country', countryCode: '', marketId: '', position: 'right', audiences: [...WIDGET_AUDIENCES], locale: '', consentText: '' };
}

/** Seed the editable form from the server view. */
export function widgetFormFromView(view: WidgetEmbedView): WidgetForm {
  return {
    name: view.name,
    origins: view.allowedOrigins.length > 0 ? [...view.allowedOrigins] : [''],
    scope: view.marketId !== undefined && view.marketId !== '' ? 'city' : 'country',
    countryCode: view.countryCode ?? '',
    marketId: view.marketId ?? '',
    position: view.settings.position,
    audiences: [...view.settings.audiences],
    locale: view.settings.locale ?? '',
    consentText: view.settings.consentText ?? '',
  };
}

/** The wire shape of a valid form: exactly what `POST /v1/config/widgets` takes (the settings replace the stored ones). */
export interface WidgetFormValue {
  readonly name: string;
  readonly allowedOrigins: readonly string[];
  readonly scope: WidgetScope;
  /** A `country` embed's country. */
  readonly countryCode?: string;
  /** A `city` embed's market. */
  readonly marketId?: string;
  readonly settings: { readonly position: WidgetPosition; readonly audiences: readonly WidgetAudience[]; readonly locale?: string; readonly consentText?: string };
}

const ORIGIN_CODE: Readonly<Record<string, WidgetFormIssueCode>> = {
  WIDGET_ORIGIN_WILDCARD: 'ORIGIN_WILDCARD',
  WIDGET_ORIGIN_INSECURE: 'ORIGIN_INSECURE',
  WIDGET_ORIGIN_MALFORMED: 'ORIGIN_MALFORMED',
};

/**
 * Validate the whole form with the kernel's own rules and collect EVERY issue (so the operator sees them all at once).
 * Origins are normalised (lowercase, default port dropped) and de-duplicated in order, exactly as the server stores them.
 */
export function validateWidgetForm(form: WidgetForm): Result<WidgetFormValue, readonly WidgetFormIssue[]> {
  const issues: WidgetFormIssue[] = [];
  const name = validatePlainText(form.name, { maxChars: WIDGET_NAME_MAX_CHARS, allowNewline: false });
  if (!name.ok) issues.push({ field: 'name', code: 'NAME_INVALID' });

  const origins: string[] = [];
  form.origins.forEach((raw, index) => {
    if (raw.trim() === '') return;
    const n = normalizeOrigin(raw);
    if (!n.ok) issues.push({ field: 'origins', code: ORIGIN_CODE[n.error.code] ?? 'ORIGIN_MALFORMED', index });
    else if (!origins.includes(n.value)) origins.push(n.value);
  });
  if (!issues.some((i) => i.field === 'origins')) {
    if (origins.length === 0) issues.push({ field: 'origins', code: 'ORIGINS_EMPTY' });
    else if (origins.length > WIDGET_MAX_ORIGINS) issues.push({ field: 'origins', code: 'TOO_MANY_ORIGINS' });
  }

  // D853: a COUNTRY embed names its country (each conversation resolves its own city); D850 (L5-002), kept for a CITY embed: without a market the conversations open at
  // the platform and the city team is refused them.
  const marketId = form.marketId.trim().toLowerCase();
  const countryCode = form.countryCode.trim().toUpperCase();
  if (form.scope === 'city') {
    if (marketId === '') issues.push({ field: 'marketId', code: 'MARKET_REQUIRED' });
    else if (!normalizeEmbedMarket(marketId).ok) issues.push({ field: 'marketId', code: 'MARKET_INVALID' });
  } else if (countryCode === '') issues.push({ field: 'countryCode', code: 'COUNTRY_REQUIRED' });
  else if (!/^[A-Z]{2}$/.test(countryCode)) issues.push({ field: 'countryCode', code: 'COUNTRY_INVALID' });

  // The operator's order is kept (the widget offers the audiences in it), duplicates dropped — as the kernel does.
  const audiences = [...new Set(form.audiences)].filter((a) => (WIDGET_AUDIENCES as readonly string[]).includes(a));
  if (audiences.length === 0) issues.push({ field: 'audiences', code: 'AUDIENCES_EMPTY' });
  const locale = form.locale.trim();
  if (locale !== '' && !normalizeEmbedSettings({ locale }).ok) issues.push({ field: 'locale', code: 'LOCALE_INVALID' });
  let consentText: string | undefined;
  if (form.consentText.trim() !== '') {
    const c = validatePlainText(form.consentText, { maxChars: WIDGET_CONSENT_TEXT_MAX_CHARS, allowNewline: true });
    if (!c.ok) issues.push({ field: 'consentText', code: 'CONSENT_INVALID' });
    else consentText = c.value;
  }
  if (!(WIDGET_POSITIONS as readonly string[]).includes(form.position)) issues.push({ field: 'settings', code: 'SETTINGS_INVALID' });
  if (issues.length > 0 || !name.ok) return err(issues);
  return ok({
    name: name.value,
    allowedOrigins: origins,
    scope: form.scope,
    ...(form.scope === 'city' ? { marketId } : { countryCode }),
    settings: { position: form.position, audiences, ...(locale !== '' ? { locale } : {}), ...(consentText !== undefined ? { consentText } : {}) },
  });
}

/** The create body of a valid form. */
export function createInputOf(value: WidgetFormValue): CreateWidgetEmbedInput {
  return {
    name: value.name,
    allowedOrigins: [...value.allowedOrigins],
    scope: value.scope,
    ...(value.scope === 'city' && value.marketId !== undefined ? { marketId: value.marketId } : {}),
    ...(value.scope === 'country' && value.countryCode !== undefined ? { countryCode: value.countryCode } : {}),
    settings: { ...value.settings, audiences: [...value.settings.audiences] },
  };
}

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);
const settingsKey = (s: WidgetFormValue['settings'] | WidgetEmbedView['settings']): string =>
  JSON.stringify({ p: s.position, a: [...s.audiences], l: s.locale ?? null, c: s.consentText ?? null });

/**
 * The minimal PATCH from the stored view to a valid form — only the members that changed, always with the view's
 * `expectedRevision` (a concurrent change answers 409). `undefined` when nothing changed.
 */
export function widgetPatchOf(view: WidgetEmbedView, value: WidgetFormValue): UpdateWidgetEmbedInput | undefined {
  const patch: { name?: string; allowedOrigins?: readonly string[]; marketId?: string; countryCode?: string; settings?: WidgetFormValue['settings'] } = {};
  if (value.name !== view.name) patch.name = value.name;
  if (!sameList(value.allowedOrigins, view.allowedOrigins)) patch.allowedOrigins = [...value.allowedOrigins];
  // D853: a city embed's market can change (never be removed); a country embed's country can change. The scope itself is fixed once the embed exists.
  if (value.scope === 'city' && value.marketId !== undefined && value.marketId !== (view.marketId ?? '')) patch.marketId = value.marketId;
  if (value.scope === 'country' && view.marketId === undefined && value.countryCode !== undefined && value.countryCode !== (view.countryCode ?? '')) patch.countryCode = value.countryCode;
  if (settingsKey(value.settings) !== settingsKey(view.settings)) patch.settings = value.settings;
  if (Object.keys(patch).length === 0) return undefined;
  return { ...patch, ...(view.revision !== undefined ? { expectedRevision: view.revision } : {}) } as UpdateWidgetEmbedInput;
}

/** Map a server refusal of a create/patch to the form field it concerns (the server does not say which origin row). */
export function widgetIssueOfReason(reasonCode: string | undefined): WidgetFormIssue | undefined {
  switch (reasonCode) {
    case 'WIDGET_NAME_INVALID':
      return { field: 'name', code: 'NAME_INVALID' };
    case 'WIDGET_ORIGIN_MALFORMED':
      return { field: 'origins', code: 'ORIGIN_MALFORMED' };
    case 'WIDGET_ORIGIN_WILDCARD':
      return { field: 'origins', code: 'ORIGIN_WILDCARD' };
    case 'WIDGET_ORIGIN_INSECURE':
      return { field: 'origins', code: 'ORIGIN_INSECURE' };
    case 'WIDGET_ORIGINS_EMPTY':
      return { field: 'origins', code: 'ORIGINS_EMPTY' };
    case 'WIDGET_TOO_MANY_ORIGINS':
      return { field: 'origins', code: 'TOO_MANY_ORIGINS' };
    case 'WIDGET_MARKET_REQUIRED':
      return { field: 'marketId', code: 'MARKET_REQUIRED' };
    case 'WIDGET_MARKET_INVALID':
      return { field: 'marketId', code: 'MARKET_INVALID' };
    case 'WIDGET_COUNTRY_REQUIRED':
      return { field: 'countryCode', code: 'COUNTRY_REQUIRED' };
    case 'WIDGET_COUNTRY_INVALID':
      return { field: 'countryCode', code: 'COUNTRY_INVALID' };
    case 'WIDGET_SETTINGS_INVALID':
      return { field: 'settings', code: 'SETTINGS_INVALID' };
    default:
      return undefined;
  }
}

/** What this operator may do with embeds (unknown grants → the server decides). */
export function widgetAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: true, mayManage: true };
  return {
    mayView: grantsAllow(grants, { permission: 'widget:view', target: 'PLATFORM' }),
    mayManage: grantsAllow(grants, { permission: 'widget:manage', target: 'PLATFORM' }),
  };
}

/**
 * What the detail shows where the code goes: the snippet ready to copy, a "rotate the key to show the code" hint (an embed
 * created before m0134 has no retrievable key), or nothing because the embed is revoked (read-only for ever).
 */
export type WidgetSnippetState = { readonly kind: 'ready'; readonly snippet: string } | { readonly kind: 'rotate_to_show' } | { readonly kind: 'revoked' };

export function widgetSnippetState(view: Pick<WidgetEmbedView, 'status' | 'snippet'>): WidgetSnippetState {
  if (view.status === 'REVOKED') return { kind: 'revoked' };
  return view.snippet !== undefined && view.snippet !== '' ? { kind: 'ready', snippet: view.snippet } : { kind: 'rotate_to_show' };
}

/**
 * D850 (L5-002), refined by D853: an ACTIVE embed with neither a country nor a market opens its conversations at the platform, where the market's agents are refused them (and no first-response
 * clock is fair). The Admin says so in red on the row and in the record until a market is chosen.
 */
export function widgetNeedsMarket(view: Pick<WidgetEmbedView, 'status' | 'marketId'> & { readonly countryCode?: string }): boolean {
  // D853: a COUNTRY embed (a country, no market) is complete - each conversation resolves its own city.
  return view.status === 'ACTIVE' && (view.marketId === undefined || view.marketId === '') && (view.countryCode === undefined || view.countryCode === '');
}

export function widgetStatusTone(status: WidgetEmbedView['status']): 'success' | 'danger' {
  return status === 'ACTIVE' ? 'success' : 'danger';
}

/** The list cell of the allowed sites: the first origin without its scheme, and how many more there are. */
export function originsSummary(origins: readonly string[]): { readonly first: string; readonly more: number } {
  const first = origins[0] ?? '';
  return { first: first.replace(/^https?:\/\//, ''), more: Math.max(0, origins.length - 1) };
}
